import React, { useState } from "react";
import { Tabs, Card } from "antd";
import PageHeader from "../Layout/PageHeader";
import BillingStatement from "./BillingStatement";
import BillingChargeList from "./BillingChargeList";
import BillingTransactionList from "./BillingTransactionList";
import BillingNoteList from "./BillingNoteList";
import BillingNoteImport from "./BillingNoteImport";
import ContraReport from "./ContraReport";
import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";

const { TabPane } = Tabs;

// PD-2336: consolidates the previously-separate Billing Statements / Billing Charges /
// Billing Transactions / Credit Notes / Debit Notes pages into one page with tabs, mirroring
// the sub-tab pattern already used by UserBilling.js on the User Details "BILLING" tab.
const BillingPage = () => {
  const { permissions } = usePermissions();
  const [activeTab, setActiveTab] = useState("invoices");
  const [transactionsStatementId, setTransactionsStatementId] = useState(undefined);
  const [chargesStatementId, setChargesStatementId] = useState(undefined);
  const [creditNoteRefreshKey, setCreditNoteRefreshKey] = useState(0);
  const [debitNoteRefreshKey, setDebitNoteRefreshKey] = useState(0);

  // Invoices row -> "Transactions" link: jump to the Transactions tab pre-filtered to that
  // statement's settlement history, matching the ticket's "opens a statement ... settlement
  // history" walkthrough.
  const handleViewTransactions = (statementId) => {
    setTransactionsStatementId(statementId);
    setActiveTab("transactions");
  };

  // Invoices row -> "Charges" link: jump to the Charges tab pre-filtered to that statement's
  // line items -- the other half of "opens a statement for its line items and settlement
  // history", mirroring handleViewTransactions above.
  const handleViewCharges = (statementId) => {
    setChargesStatementId(statementId);
    setActiveTab("charges");
  };

  return (
    <div>
      <PageHeader title="Billing" childRoutes={[{ path: "/billing", breadcrumbName: "Billing" }]} />

      <Card bodyStyle={{ padding: "8px 24px 24px" }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} size="large" tabBarGutter={32}>
          {permissions?.includes(PERMISSIONS.VIEW_BILLING_STATEMENT_PAGE) && (
            <TabPane tab="Invoices" key="invoices">
              <BillingStatement onViewTransactions={handleViewTransactions} onViewCharges={handleViewCharges} />
            </TabPane>
          )}
          {permissions?.includes(PERMISSIONS.VIEW_BILLING_STATEMENT_PAGE) && (
            <TabPane tab="Charges" key="charges">
              <BillingChargeList initialStatementId={chargesStatementId} />
            </TabPane>
          )}
          {permissions?.includes(PERMISSIONS.VIEW_BILLING_STATEMENT_PAGE) && (
            <TabPane tab="Transactions" key="transactions">
              <BillingTransactionList initialStatementId={transactionsStatementId} />
            </TabPane>
          )}
          {permissions?.includes(PERMISSIONS.VIEW_BILLING_NOTES_PAGE) && (
            <TabPane tab="CN" key="credit-notes">
              <BillingNoteList
                refreshSignal={creditNoteRefreshKey}
                noteType="CREDIT"
                title="Credit Notes"
                apiBase="/api/credit-notes"
                showHeader={false}
              >
                {permissions?.includes(PERMISSIONS.GENERATE_BILLING_NOTE) && (
                  <BillingNoteImport
                    noteType="CREDIT"
                    title="Credit Notes"
                    onImported={() => setCreditNoteRefreshKey((k) => k + 1)}
                  />
                )}
              </BillingNoteList>
            </TabPane>
          )}
          {permissions?.includes(PERMISSIONS.VIEW_BILLING_NOTES_PAGE) && (
            <TabPane tab="DN" key="debit-notes">
              <BillingNoteList
                refreshSignal={debitNoteRefreshKey}
                noteType="DEBIT"
                title="Debit Notes"
                apiBase="/api/debit-notes"
                showHeader={false}
              >
                {permissions?.includes(PERMISSIONS.GENERATE_BILLING_NOTE) && (
                  <BillingNoteImport
                    noteType="DEBIT"
                    title="Debit Notes"
                    onImported={() => setDebitNoteRefreshKey((k) => k + 1)}
                  />
                )}
              </BillingNoteList>
            </TabPane>
          )}
          {permissions?.includes(PERMISSIONS.VIEW_BILLING_STATEMENT_PAGE) && (
            <TabPane tab="Contra Report" key="contra-report">
              <ContraReport />
            </TabPane>
          )}
        </Tabs>
      </Card>
    </div>
  );
};

export default BillingPage;
